import type {
  ActionPhase,
  ActionPhases,
  ActiveAction,
  PresentationEvent,
  PresentationFrame,
  PresentationTrace,
  SampledEntity,
  Vec3,
} from "./types";

const IMPACT_LIFETIME = 0.42;
const RECENT_EVENT_WINDOW = 1.25;

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function lerp(from: Vec3, to: Vec3, progress: number): Vec3 {
  const t = clamp01(progress);
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
    z: from.z + (to.z - from.z) * t,
  };
}

export function actionDuration(phases: ActionPhases): number {
  return phases.telegraph + phases.commit + phases.impact + phases.recover;
}

function actionAt(event: Extract<PresentationEvent, { type: "act" }>, time: number): ActiveAction | null {
  const elapsed = time - event.at;
  const total = actionDuration(event.phases);
  if (elapsed < 0 || elapsed >= total) return null;

  const phaseOrder: ActionPhase[] = ["telegraph", "commit", "impact", "recover"];
  let phaseStart = 0;
  for (const phase of phaseOrder) {
    const duration = event.phases[phase];
    const phaseEnd = phaseStart + duration;
    if (elapsed < phaseEnd || phase === "recover") {
      return {
        eventId: event.id,
        action: event.action,
        targetId: event.targetId,
        phase,
        phaseProgress: duration === 0 ? 1 : clamp01((elapsed - phaseStart) / duration),
        totalProgress: total === 0 ? 1 : clamp01(elapsed / total),
        tags: event.tags ?? [],
        motion: event.motion,
      };
    }
    phaseStart = phaseEnd;
  }
  return null;
}

export function validateTrace(trace: PresentationTrace): string[] {
  const errors: string[] = [];
  const entityIds = new Set<string>();
  const eventIds = new Set<string>();

  if (trace.duration <= 0) errors.push("Trace duration must be positive.");
  for (const entity of trace.entities) {
    if (entityIds.has(entity.id)) errors.push(`Duplicate entity id: ${entity.id}`);
    entityIds.add(entity.id);
    if (entity.dimensions && (entity.dimensions.x <= 0 || entity.dimensions.y <= 0 || entity.dimensions.z <= 0)) {
      errors.push(`Entity ${entity.id} dimensions must be positive.`);
    }
    if (entity.vitals && (entity.vitals.maxHealth <= 0 || entity.vitals.health < 0)) {
      errors.push(`Entity ${entity.id} vitals must have non-negative health and positive maxHealth.`);
    }
  }
  for (const event of trace.events) {
    if (eventIds.has(event.id)) errors.push(`Duplicate event id: ${event.id}`);
    eventIds.add(event.id);
    if (event.at < 0 || event.at > trace.duration) errors.push(`Event ${event.id} falls outside the trace.`);

    const refs = referencedEntities(event);
    for (const ref of refs) {
      if (!entityIds.has(ref)) errors.push(`Event ${event.id} references unknown entity ${ref}.`);
    }
    if (event.type === "move" && event.duration <= 0) errors.push(`Move ${event.id} must have positive duration.`);
    if (event.type === "act" && actionDuration(event.phases) <= 0) errors.push(`Action ${event.id} must have positive duration.`);
    if (event.type === "act" && event.motion) {
      for (const key of ["energy", "reach", "advance", "guard", "targetHeight"] as const) {
        if (!Number.isFinite(event.motion[key]) || event.motion[key] < 0 || event.motion[key] > 1) {
          errors.push(`Action ${event.id} motion.${key} must be finite and within 0..1.`);
        }
      }
      if (event.motion.direction !== -1 && event.motion.direction !== 1) errors.push(`Action ${event.id} motion.direction must be -1 or 1.`);
      if (event.motion.hand !== "left" && event.motion.hand !== "right") errors.push(`Action ${event.id} motion.hand is invalid.`);
      if (event.motion.delivery !== "swing" && event.motion.delivery !== "thrust" && event.motion.delivery !== "bash") {
        errors.push(`Action ${event.id} motion.delivery is invalid.`);
      }
      if (event.motion.plane !== "vertical" && event.motion.plane !== "diagonal" && event.motion.plane !== "horizontal") {
        errors.push(`Action ${event.id} motion.plane is invalid.`);
      }
    }
    if (event.type === "impact" && event.motion) {
      const { direction, magnitude, pointHeight } = event.motion;
      if (![direction.x, direction.y, direction.z].every(Number.isFinite)) {
        errors.push(`Impact ${event.id} motion.direction must be finite.`);
      }
      if (!Number.isFinite(magnitude) || magnitude < 0 || magnitude > 1) {
        errors.push(`Impact ${event.id} motion.magnitude must be finite and within 0..1.`);
      }
      if (pointHeight !== undefined && (!Number.isFinite(pointHeight) || pointHeight < 0 || pointHeight > 1)) {
        errors.push(`Impact ${event.id} motion.pointHeight must be finite and within 0..1.`);
      }
    }
  }
  return errors;
}

function referencedEntities(event: PresentationEvent): string[] {
  switch (event.type) {
    case "spawn":
    case "move":
    case "status":
    case "despawn":
      return [event.entityId];
    case "act":
      return event.targetId ? [event.entityId, event.targetId] : [event.entityId];
    case "impact":
      return [event.sourceId, event.targetId];
  }
}

export function assertValidTrace(trace: PresentationTrace): PresentationTrace {
  const errors = validateTrace(trace);
  if (errors.length > 0) throw new Error(errors.join("\n"));
  return trace;
}

export function sampleTrace(trace: PresentationTrace, requestedTime: number): PresentationFrame {
  const time = Math.max(0, Math.min(trace.duration, requestedTime));
  const entities = new Map<string, SampledEntity>();
  for (const entity of trace.entities) {
    entities.set(entity.id, {
      ...entity,
      position: { ...entity.position },
      visible: false,
      movement: null,
      action: null,
      statuses: [],
    });
  }

  const events = [...trace.events].sort((a, b) => a.at - b.at || a.id.localeCompare(b.id));
  for (const event of events) {
    const elapsed = time - event.at;
    if (elapsed < 0) continue;

    if (event.type === "spawn") {
      const entity = entities.get(event.entityId)!;
      entity.visible = true;
      entity.position = { ...(event.position ?? entity.position) };
    } else if (event.type === "despawn") {
      entities.get(event.entityId)!.visible = false;
    } else if (event.type === "move") {
      const entity = entities.get(event.entityId)!;
      const progress = clamp01(elapsed / event.duration);
      entity.position = lerp(event.from, event.to, progress);
      if (progress < 1) entity.movement = { gait: event.gait ?? "walk", progress };
    } else if (event.type === "act") {
      const active = actionAt(event, time);
      if (active) entities.get(event.entityId)!.action = active;
    } else if (event.type === "status") {
      const entity = entities.get(event.entityId)!;
      const statuses = new Set(entity.statuses);
      if (event.active) statuses.add(event.status);
      else statuses.delete(event.status);
      entity.statuses = [...statuses].sort();
    }
  }

  const impacts = events
    .filter((event): event is Extract<PresentationEvent, { type: "impact" }> => event.type === "impact")
    .filter((event) => time >= event.at && time - event.at <= IMPACT_LIFETIME)
    .map((event) => ({
      eventId: event.id,
      sourceId: event.sourceId,
      targetId: event.targetId,
      outcome: event.outcome,
      amount: event.amount,
      effect: event.effect,
      motion: event.motion,
      age: time - event.at,
    }));

  return {
    time,
    duration: trace.duration,
    entities: [...entities.values()],
    impacts,
    recentEvents: events.filter((event) => time >= event.at && time - event.at <= RECENT_EVENT_WINDOW),
  };
}
